import { useSponsoredPlacements } from '../../features/ads/useSponsoredPlacements';
import { exploreRecommendations } from './exploreRecommendations';
import AppUiScope from '../../app-ui/AppUiScope';
import baseline, { appTextStyle } from '../../app-ui/baseline';
import SupportPromotionCard from '../../features/sponsorship/SupportPromotionCard';
import { SponsoredAlbumRail, SponsoredCollections } from '../../features/ads/SponsoredContent';
import QuickDestinations from '../../components/responsive/QuickDestinations';
import React, { useState } from 'react';
import { AccountBoundary } from '../../features/settings/shared';
import { useUserStore } from '../../stores/userStore';
import { useBrowseSession, useBrowsePosition } from '../../features/browse/useBrowseList';
import ExploreState from './ExploreState';
import useExploreShelves from './useExploreShelves';
import ContentShelf, { ShelfNavigationContext } from '../../components/responsive/ContentShelf';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { discoveryApi } from '../../api/discovery';
import PageLayout from '../../components/layout/PageLayout';
import {
  ContentState,
  DiscoverySection,
  albumPath,
} from '../../components/content/DiscoveryContent';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import SEO from '../../components/common/SEO';
import {
  RecommendationFeatureCard,
  PlaylistFeatureCard,
} from '../../components/content/ExploreFeatureCard';
import AlbumContentTitle from '../../components/content/AlbumContentTitle';
import AppAuthor from '../../app-ui/AppAuthor';
import LandingTimeline from './ExploreTimelinePreview';
import '../../styles/AppParity.css';
import './ExploreDesign.css';

export default function Explore() {
  return <AccountBoundary component={ExploreBoundary} />;
}
function ExploreBoundary({ user }) {
  const { locale } = useWebLocale();
  const memory = useBrowseSession('explore');
  if (useUserStore.getState().user?.id !== user?.id) return null;
  return (
    <ExploreView key={`${locale}:${memory.entry.version}:${memory.location.key}`} {...memory} />
  );
}
function ExploreView({ entry, returning }) {
  const { t, locale } = useWebLocale();
  const identity = [entry.version, locale];
  const [timelineReady, setTimelineReady] = useState(false);
  const queryOptions = {
    retry: false,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  };
  const home = useQuery({
    ...queryOptions,
    queryKey: ['web-parity', 'explore', identity],
    queryFn: ({ signal }) => discoveryApi.home(signal),
  });
  const reviews = useQuery({
    ...queryOptions,
    queryKey: ['web-parity', 'recent-reviews', identity],
    queryFn: ({ signal }) => discoveryApi.recentReviews(signal),
  });
  const data = home.data || {};
  const recommendedCampaigns = useSponsoredPlacements('EXPLORE_RECOMMENDED');
  const recommendations = exploreRecommendations(
    data.recommendedReviews,
    recommendedCampaigns,
    locale === 'en' ? 'Unknown artist' : '아티스트 정보 없음',
  );
  const recent = (reviews.data ?? data.latestReviews ?? [])
    .filter((item) => item.content?.trim())
    .slice(0, 15);
  const position = useBrowsePosition({
    entry,
    returning,
    ready:
      !home.isPending &&
      !home.isFetching &&
      !reviews.isPending &&
      !reviews.isFetching &&
      (!entry.timelineEnabled || timelineReady),
  });
  useExploreShelves(position.ref, entry, returning);
  return (
    <PageLayout paddingTop={{ xs: '12px', md: '8px' }}>
      <ShelfNavigationContext.Provider value="edges">
        <div {...position}>
          <AppUiScope className="pp-parity pp-explore-design" surface="explore">
            <SEO title={t('explore')} />
            <div className="pp-explore-heading">
              <h1 className="pp-visually-hidden">{t('explore')}</h1>
              <QuickDestinations showChevron />
            </div>
            <ExploreState query={home}>
              <DiscoverySection inlineMore title={t('latestKr')} to="/albums?region=korean">
                <SponsoredAlbumRail
                  placementKey="EXPLORE_LATEST_KR"
                  label={t('latestKr')}
                  items={data.koreanMusicReviews?.slice(0, 12)}
                />
              </DiscoverySection>
              <DiscoverySection inlineMore title={t('latestGlobal')} to="/albums?region=global">
                <SponsoredAlbumRail
                  placementKey="EXPLORE_LATEST_GLOBAL"
                  label={t('latestGlobal')}
                  items={data.globalMusicReviews?.slice(0, 12)}
                />
              </DiscoverySection>
              {data.globalMusicReviews?.length > 0 && (
                <SupportPromotionCard surface="EXPLORE" afterSection="latest" />
              )}
              <DiscoverySection
                inlineMore
                title={t('recommended')}
                to="/albums?category=recommended"
              >
                <ContentShelf
                  navigation="dots"
                  profile="features"
                  className="pp-app-feature-shelf"
                  label={t('recommended')}
                >
                  {recommendations.map(({ item, campaign }) => (
                    <RecommendationFeatureCard
                      item={item}
                      campaign={campaign}
                      key={campaign ? `ad-${campaign.id}` : item.albumId}
                    />
                  ))}
                </ContentShelf>
              </DiscoverySection>
            </ExploreState>
            <SponsoredCollections placementKey="EXPLORE_RECOMMENDED" />
            <DiscoverySection inlineMore title={t('recentReviews')} to="/reviews">
              <ExploreState query={reviews} fallback={data.latestReviews}>
                {recent.length ? (
                  <div style={{ '--review-column-count': Math.ceil(recent.length / 3) }}>
                    <ContentShelf navigation="dots" profile="reviews" label={t('recentReviews')}>
                      {recent.map((item) => (
                        <Link
                          className="pp-review-card pp-explore-review"
                          key={item.reviewId}
                          to={`${albumPath(item)}/user-reviews/${item.reviewId}`}
                        >
                          <p className="pp-explore-quote">
                            <svg
                              className="pp-explore-quote-icon"
                              width="12"
                              height="12"
                              viewBox="0 0 14 14"
                              aria-hidden="true"
                            >
                              <path
                                fill="currentColor"
                                d="M0 7.1C0 3.7 1.7 1.3 5 0l.8 1.8C4 2.6 3.1 3.8 2.9 5.2h2.8V10H0V7.1Zm8.2 0c0-3.4 1.7-5.8 5-7.1l.8 1.8c-1.8.8-2.7 2-2.9 3.4H14V10H8.2V7.1Z"
                              />
                            </svg>
                            <span
                              className="pp-explore-quote-text"
                              style={appTextStyle(
                                baseline.explore.reviewBody,
                                baseline.contentText.preview,
                              )}
                            >
                              {item.content}
                            </span>
                          </p>
                          <div className="pp-review-meta">
                            <img src={item.albumImageUrl || undefined} alt="" loading="lazy" />
                            <div>
                              <AlbumContentTitle item={item} title={item.albumTitle} />
                              <span className="pp-secondary pp-content-artist">
                                {item.artist?.name || item.albumArtistName}
                              </span>
                              <span className="pp-explore-reviewer">
                                <AppAuthor
                                  name={item.userNickname}
                                  image={item.userProfileImageUrl}
                                  mark={item.userProfileMark}
                                />
                                <span
                                  className="pp-explore-review-date"
                                  style={appTextStyle(baseline.contentText.metadata)}
                                >
                                  · {item.createdAt}
                                </span>
                              </span>
                            </div>
                            <span
                              className="pp-rating pp-explore-review-score"
                              style={appTextStyle(baseline.contentText.score)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                                <path
                                  fill="#f16438"
                                  transform="translate(0 0.955)"
                                  d="M12 2L14.47 8.6L21.51 8.91L15.99 13.3L17.88 20.09L12 16.2L6.12 20.09L8.01 13.3L2.49 8.91L9.53 8.6L12 2Z"
                                />
                              </svg>
                              {Number(item.rating || 0).toFixed(1)}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </ContentShelf>
                  </div>
                ) : (
                  <p className="pp-secondary">{t('empty')}</p>
                )}
              </ExploreState>
            </DiscoverySection>
            {!reviews.isPending && recent.length > 0 && (
              <SupportPromotionCard surface="EXPLORE" afterSection="reviews" />
            )}
            <DiscoverySection inlineMore title={t('memberPlaylists')} to="/lists">
              <ContentState query={home}>
                {data.latestLists?.length ? (
                  <ContentShelf
                    navigation="dots"
                    profile="features"
                    className="pp-app-feature-shelf"
                    label={t('memberPlaylists')}
                  >
                    {data.latestLists.slice(0, 5).map((item) => (
                      <PlaylistFeatureCard item={item} key={item.id} />
                    ))}
                  </ContentShelf>
                ) : (
                  <p className="pp-secondary">{t('empty')}</p>
                )}
              </ContentState>
            </DiscoverySection>
            {!home.isPending && data.latestLists?.length > 0 && (
              <SupportPromotionCard surface="EXPLORE" afterSection="playlists" />
            )}
            <LandingTimeline entry={entry} identity={identity} onReady={setTimelineReady} />
          </AppUiScope>
        </div>
      </ShelfNavigationContext.Provider>
    </PageLayout>
  );
}
